import {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import { getRoomFromDatabase, saveRoomToDatabase } from "../hooks/api";

const RoomContext = createContext();

export const RoomProvider = ({ children }) => {
  const [roomData, setRoomData] = useState(() => {
    const stored = localStorage.getItem("childRoom");
    return stored
      ? JSON.parse(stored)
      : { wallIndex: 0, itemsArray: [], itemPositions: {} };
  });

  // Blocks DB saves until the initial server GET completes.
  // Prevents the empty initial state from overwriting saved positions.
  const isHydratedRef = useRef(false);

  const loadRoom = useRef((childId) => {
    isHydratedRef.current = false;
    getRoomFromDatabase(childId)
      .then((data) => {
        const fresh = {
          wallIndex: data.wall_index ?? 0,
          itemsArray: data.room_items ?? [],
          itemPositions: data.item_positions ?? {},
        };
        setRoomData(fresh);
        localStorage.setItem("childRoom", JSON.stringify(fresh));
      })
      .catch((err) => console.error("No se pudo cargar el cuarto:", err))
      .finally(() => {
        isHydratedRef.current = true;
      });
  });

  // Load from server on mount or when childId changes
  useEffect(() => {
    const childId = localStorage.getItem("childId");
    if (!childId) return;
    loadRoom.current(childId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localStorage.getItem("childId")]);

  // Cross-tab: handle childId changes from other tabs/windows
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== "childId") return;
      if (e.newValue) {
        loadRoom.current(e.newValue);
      } else {
        isHydratedRef.current = false;
        setRoomData({ wallIndex: 0, itemsArray: [], itemPositions: {} });
        localStorage.removeItem("childRoom");
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Sync roomData → localStorage + DB (only after initial server load)
  useEffect(() => {
    localStorage.setItem("childRoom", JSON.stringify(roomData));

    if (!isHydratedRef.current) return;

    const childId = localStorage.getItem("childId");
    if (!childId) return;

    saveRoomToDatabase(roomData.wallIndex, roomData.itemsArray, roomData.itemPositions || {})
      .catch((err) => console.error("Error al guardar el cuarto:", err));
  }, [roomData]);

  const value = useMemo(() => ({ roomData, setRoomData }), [roomData]);

  return (
    <RoomContext.Provider value={value}>
      {children}
    </RoomContext.Provider>
  );
};

RoomProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useRoom = () => useContext(RoomContext);
